import { AppLoadingSkeletonRemover } from "@components/utils/AppLoadingSkeleton";
import AppUpdateDetector from "@components/utils/AppUpdateDetector";
import ErrorFallback from "@components/utils/ErrorFallback";
import MemberSettingsLoader from "@components/utils/MemberSettingsLoader";
import ThemeColorLoader from "@components/utils/ThemeColorLoader";
import { AppStatsigProvider } from "@configs/statsig/StatsigProvider";
import useCrashSessionCleanup from "@hooks/useCrashSessionCleanup";
import { ThemePreset } from "@configs/theme-preset";
import { useMemberSettings } from "@hooks/useMemberSettings";
import { GoogleOAuthProvider } from "@react-oauth/google";
import * as Sentry from "@sentry/react";
import { store } from "@stores";
import "@styles/App.css";
import { initializeFirebase, isFirebaseAvailable } from "@utils/firebase";
import { ThemeProvider } from "kz-ui-sdk";
import "kz-ui-sdk/dist/sdk-themes.css";
import { PropsWithChildren, useEffect } from "react";
import "react-aspect-ratio/aspect-ratio.css";
import { Provider } from "react-redux";
import AppContent from "./AppContent";
import "./configs/i18n";

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;
const STATSIG_CLIENT_KEY = import.meta.env.VITE_STATSIG_CLIENT_KEY;

function App() {
  useCrashSessionCleanup();

  return (
    <ThemeProvider
      value={{
        preset: ThemePreset,
      }}
    >
      <Provider store={store}>
        <WithStatsigProvider>
          <ThemeColorLoader />
          <AppLoadingSkeletonRemover />
          <MemberSettingsLoader>
            <FirebaseInitializer />
            <WithGoogleOAuthProvider>
              <AppContent />
            </WithGoogleOAuthProvider>
          </MemberSettingsLoader>
        </WithStatsigProvider>
        <AppUpdateDetector />
      </Provider>
    </ThemeProvider>
  );
}

/**
 * Initialize Firebase if available
 */
const FirebaseInitializer = () => {
  const { googleAuthEnabled } = useMemberSettings();

  useEffect(() => {
    if (googleAuthEnabled && isFirebaseAvailable()) {
      initializeFirebase();
    }
  }, [googleAuthEnabled]);

  return null;
};

/**
 * Conditionally wrap children in GoogleOAuthProvider if Google OAuth is enabled
 * (but not using Firebase Auth)
 */
const WithGoogleOAuthProvider = ({ children }: PropsWithChildren) => {
  const { googleAuthEnabled } = useMemberSettings();
  const useFirebase = googleAuthEnabled && isFirebaseAvailable();

  // Only use GoogleOAuthProvider if Google Auth is enabled but Firebase is not available
  if (googleAuthEnabled && !useFirebase && GOOGLE_CLIENT_ID) {
    return <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>{children}</GoogleOAuthProvider>;
  }
  return children;
};

/**
 * Conditionally wrap children in StatsigProvider if Statsig client key is available
 */
const WithStatsigProvider = ({ children }: PropsWithChildren) => {
  return <AppStatsigProvider>{children}</AppStatsigProvider>;
};

export const EnhancedApp = Sentry.withErrorBoundary(App, {
  fallback: ({ error, resetError }: { error: unknown; resetError: () => void }) => (
    <ErrorFallback
      error={error}
      resetError={resetError}
    />
  ),
});

export default App;
